import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Eye, ShoppingBag, Clock } from 'lucide-react';
import Avatar from '@/components/Avatar';
import useLongPress from '@/hooks/useLongPress';
import CardPeekSheet from '@/components/CardPeekSheet';

/**
 * Card for a buyer's requirement post. Distinguishable from ProduceCard
 * by the "Requirement" badge + shopping-bag glyph and an inverted price pill
 * (buyer's offer instead of seller's ask).
 */
export default function RequirementCard({ post, index = 0 }) {
  const navigate = useNavigate();
  const [peekOpen, setPeekOpen] = useState(false);

  const bind = useLongPress({
    onLongPress: () => setPeekOpen(true),
    onClick: () => navigate(`/requirement/${post.id}`),
  });

  const timeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 60) return `${Math.max(diff, 1)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <>
    <div
      data-testid={`requirement-card-${post.id}`}
      {...bind}
      style={{ animationDelay: `${index * 0.05}s`, opacity: 0, touchAction: 'pan-y', WebkitUserSelect: 'none', userSelect: 'none' }}
      className="group bg-[#F6F8FB] rounded-3xl border border-[#E4E8EF] overflow-hidden hover:border-[#0B4F3F]/25 hover:shadow-[0_20px_60px_-25px_rgba(11,79,63,0.15)] active:scale-[0.99] transition-all duration-300 cursor-pointer animate-fade-in-up select-none"
    >
      {/* Header strip — replaces the wide image on ProduceCard so requirements
          feel distinctly different (lighter, text-first). */}
      <div className="relative px-5 pt-5 pb-3 border-b border-[#E4E8EF]/60">
        <div className="flex items-center justify-between gap-3">
          <span
            data-testid="requirement-badge"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B4F3F]/10 text-[#0B4F3F] text-[10px] font-bold uppercase tracking-wider"
          >
            <ShoppingBag className="w-3 h-3" />
            Requirement
          </span>
          <span className="text-[10px] text-[#5B6675] flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timeAgo(post.created_at)}
          </span>
        </div>

        {post.image && (
          <div className="mt-3 w-full h-40 rounded-2xl overflow-hidden bg-[#EFF2F7]">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-[#0B1620] line-clamp-2 font-['Bricolage_Grotesque'] tracking-tight">
            {post.title}
          </h3>
          <span className="text-[11px] font-semibold text-[#0B4F3F] whitespace-nowrap tabular-nums">
            {post.quantity} {post.unit}
          </span>
        </div>

        {post.description && (
          <p className="text-[13px] text-[#5B6675] line-clamp-2 leading-relaxed">
            {post.description}
          </p>
        )}

        {/* Offer pill — inverted (bordered) so it doesn't look like a produce price */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-baseline gap-0.5 px-3 py-1.5 rounded-full border border-[#0B4F3F]/30 bg-[#F6F8FB] text-[#0B4F3F]">
            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">Offer</span>
            <span className="text-sm font-bold font-['Bricolage_Grotesque'] ml-1">
              ₹{post.price_per_unit}
            </span>
            <span className="text-[10px] font-semibold opacity-70">/{post.unit}</span>
          </span>
          <span className="px-2 py-1 rounded-full bg-[#EFF2F7] text-[10px] font-semibold text-[#0B1620] uppercase tracking-wider">
            {post.category}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[#E4E8EF]/60">
          {/* Buyer info */}
          <div className="flex items-center gap-2.5">
            <Avatar name={post.user_name} src={post.user_avatar} size={32} className="ring-1 ring-[#E4E8EF]" />
            <div className="flex flex-col leading-tight">
              <span className="text-[12px] font-medium text-[#0B1620]">{post.user_name || 'Buyer'}</span>
              <span className="text-[10px] text-[#5B6675]/80 capitalize">
                {post.user_role === 'farmer' ? 'Farmer buyer' : 'Buyer'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[#5B6675]">
            {post.location && (
              <span className="text-[10px] flex items-center gap-0.5 max-w-[80px] truncate">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{post.location}</span>
              </span>
            )}
            <span className="flex items-center gap-1 text-[10px] tabular-nums">
              <Eye className="w-3 h-3" />
              {post.views || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
    <CardPeekSheet open={peekOpen} onOpenChange={setPeekOpen} kind="requirement" post={post} />
    </>
  );
}
